import { Platform } from 'models/platforms/platform';
import { Manufacturer } from 'models/platforms/manufacturer';
import {
  LGKeyMapping,
  LGOneTrustKeyMapping,
} from 'models/platforms/LGKeyMappings';
import { OneTrustRemoteKeys } from 'types/oneTrust';
import { AppData, Registry } from '@lightningjs/sdk';
import { constants, webOS } from 'aliases';
import { DeviceInfo } from 'models/platforms/deviceInfo';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import { DrmType } from 'services/drm/DRMService';
import { webOsMapping } from 'types/lg';
import { AbstractMouseDeviceIntegration } from '../AbstractMouseDeviceIntegration';

export class LGDeviceIntegration extends AbstractMouseDeviceIntegration {
  readonly platform = Platform.LG;

  private sdkVersion = '';

  constructor() {
    super();
    this.deviceInfo.manufacturer = Manufacturer.LG;
  }

  override getDeviceId(): string {
    return this.deviceId;
  }

  override hasFlickerIssue(): boolean {
    return false;
  }

  override isLowPerformanceModel(): boolean {
    return true;
  }

  override isLowMemoryModel(): boolean {
    return false;
  }

  protected override isOneTrustSupportedOs(): boolean {
    return (
      parseFloat(this.deviceInfo.osVersion) >=
      constants.oneTrust.minWebOsVersion
    );
  }

  override getAdvertisingId(): string | null {
    return null;
  }

  override getDrmType(): DrmType {
    return DrmType.PLAYREADY;
  }

  override getAppId(): string {
    return webOS.fetchAppId();
  }

  override getSubmittedAppVersion(): string {
    return '';
  }

  override async load() {
    const fetchDeviceInfo = new Promise<void>(resolve => {
      webOS.deviceInfo(async (device: any) => {
        this.deviceInfo.model = device.modelName;
        this.setSdkVersion(device.sdkVersion);
        this.deviceInfo.osVersion = this.mapOSVersion();
        this.deviceInfo.ip = await this.getDeviceIp();
        resolve();
      });
    });

    this.deviceId = await this.fetchDeviceId();

    // we don't have access to the LG ad id, so we will use the device ID
    this.deviceInfo.adId = this.deviceId;

    this._ccSettings.enabled = await this.getCcOnOffSettings();
    await fetchDeviceInfo;

    // Listen for deep links
    Registry.addEventListener(
      document,
      'webOSRelaunch',
      this.handleDeepLink.bind(this),
    );

    this.setupMouseHandling();
  }

  override onAppActive() {
    // Do nothing
  }

  override beforeAppClose(callback: (showPopUp: boolean) => void): void {
    callback(true);
  }

  override closeApp(): void {
    window.close();
  }

  override handleDeepLink(): boolean {
    try {
      const osVersion = this.getOSVersion();
      // use webOSSystem for webOS TV 5.0 or higher, else use PalmSystem
      const launchParams =
        osVersion >= 5
          ? (window as any).webOSSystem.launchParams
          : (window as any).PalmSystem.launchParams;

      if (launchParams) {
        const params = JSON.parse(launchParams);
        if (params.contentTarget) {
          // Assume URL will be in the form `[RoutePath]/[...params]`
          this.handleDeepLinkURL(params.contentTarget);
          return true;
        } else {
          // app is just relaunched nothing more, ignore this
        }
      }
    } catch (e) {
      reportError({ error: e, userFacing: true, code: ErrorCode.c10 });
    }
    return false;
  }

  override handleNetworkChange(callback: (arg: boolean) => void): void {
    Registry.addEventListener(window, 'online', function () {
      callback(true);
    });
    Registry.addEventListener(window, 'offline', function () {
      callback(false);
    });
  }

  override setScreenSaver(state: boolean): void {
    return;
  }

  override getAnnouncerEnabled(): boolean {
    return false;
  }

  override shouldAnnounceContext(): boolean {
    return true;
  }

  override shouldAnnounceSeeking(): boolean {
    return true;
  }

  override handleAnnouncerChange(callback: (arg: boolean) => void): void {
    webOS.service.request('luna://com.webos.settingsservice', {
      method: 'getSystemSettings',
      parameters: {
        category: 'option',
        keys: ['audioGuidance'],
      },
      onSuccess: (inResponse: any) => {
        callback(inResponse?.settings?.audioGuidance === 'on');
      },
      onFailure: (inError: any) => {
        console.log("Failed to get settings' value");
        console.log('[' + inError.errorCode + ']: ' + inError.errorText);
        callback(false);
      },
    });
  }

  override handleCcChange(callback: (enabled: boolean) => void): void {
    // Not Supported
  }

  override useTextTrackComponent(): boolean {
    return false;
  }

  override getKeyMapping(): Record<number, string> {
    return LGKeyMapping;
  }

  override getOneTrustKeyMapping(): OneTrustRemoteKeys {
    return {
      ...super.getOneTrustKeyMapping(),
      ...LGOneTrustKeyMapping,
    };
  }

  override onAdPodStart(): void {
    // Not Supported
  }

  override onAdPodEnd(): void {
    // Not Supported
  }

  override onPlayerExit(): void {
    // Not Supported
  }

  private setSdkVersion(sdkVersion: string): string {
    if (!sdkVersion) return this.getSdkVersion();

    const osVersionArray = sdkVersion.split('.');
    return osVersionArray[0]!;
  }

  private getSdkVersion(): string {
    return this.sdkVersion;
  }

  private mapOSVersion(): string {
    const sdkVersion = this.getSdkVersion();
    const osWebAgentMapping = webOsMapping;
    const chromeVersion = this.getChromeVersion();

    // If there is no Chromium version in the user agent, or the mapping is not found
    // will use the default sdkVersion instead
    if (!chromeVersion || !osWebAgentMapping) {
      return sdkVersion;
    }

    const webOsVersion = Number(
      Object.keys(osWebAgentMapping).find(
        key => osWebAgentMapping[Number(key)] === chromeVersion,
      ),
    );

    return webOsVersion.toString() ?? sdkVersion;
  }

  private getOSVersion(): number {
    return parseInt(this.deviceInfo.osVersion);
  }

  private fetchDeviceId(): Promise<string> {
    return new Promise((resolve, reject) => {
      webOS.service.request('luna://com.webos.service.sm', {
        method: 'deviceid/getIDs',
        parameters: {
          idType: ['LGUDID'],
        },
        onSuccess: (inResponse: any) => {
          resolve(inResponse.idList[0].idValue);
        },
        onFailure: (inError: any) => {
          console.log('Failed to get system ID information');
          console.log('[' + inError.errorCode + ']: ' + inError.errorText);
          reject(inError);
        },
      });
    });
  }

  private getCcOnOffSettings(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      webOS.service.request('luna://com.webos.settingsservice', {
        method: 'getSystemSettings',
        parameters: {
          category: 'caption',
          keys: ['captionEnable'],
        },
        onSuccess: (inResponse: any) => {
          resolve(inResponse.settings.captionEnable === 'on');
        },
        onFailure: function (inError: any) {
          console.log(
            `[LG] Failed to get CC settings value [${inError.errorCode}]: ${inError.errorText}`,
          );
          reject(inError);
        },
      });
    });
  }

  private getDeviceIp(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      webOS.service.request('luna://com.palm.connectionmanager', {
        method: 'getStatus',
        onSuccess: (inResponse: any) => {
          resolve(
            inResponse.wifi.ipAddress || inResponse.wired.ipAddress || '',
          );
        },
        onFailure: (inError: any) => {
          console.log('Failed to get network state');
          console.log('[' + inError.errorCode + ']: ' + inError.errorText);
          reject(inError);
        },
      });
    });
  }

  override setupMouseDetectionHandling() {
    Registry.addEventListener(
      document,
      'cursorStateChange',
      this._cursorVisibilityChange.bind(this),
    );
  }

  private _cursorVisibilityChange(event: any) {
    this.isHovering = event.detail.visibility;
    this.cursorVisibilityChange(event.detail.visibility);
  }

  override async getEnterKeyEvent(
    keyCodeMap: Record<number, string>,
    enterString: string,
  ) {
    const deviceInfo = await AppData?.device.getDeviceInfo();
    const osVersion = deviceInfo?.osVersion
      ? parseInt(deviceInfo.osVersion)
      : undefined;

    const keyCode = Number(
      Object.keys(keyCodeMap).find(
        key => keyCodeMap[Number(key)] === enterString,
      ),
    );

    if (osVersion !== undefined && osVersion <= 4) {
      const enterKeydownEvent = document.createEvent('KeyboardEvent');

      enterKeydownEvent.initKeyboardEvent(
        'keydown', // typeArg
        true, // bubblesArg
        true, // cancelableArg
        window, // viewArg
        enterString, // keyArg
        0, // locationArg
        false, // ctrlKey
        false, // altKey
        false, // shiftKey
        false, // metaKey
      );

      Object.defineProperty(enterKeydownEvent, 'keyCode', { value: keyCode });
      Object.defineProperty(enterKeydownEvent, 'which', { value: keyCode });

      return enterKeydownEvent;
    } else {
      return super.getEnterKeyEvent(keyCodeMap, enterString);
    }
  }

  override supportsParentalPin(): boolean {
    return true;
  }
}
